/**
 * Firebase Analytics + Performance Monitoring bootstrap for the web admin.
 *
 * This is ADDITIVE to the in-house telemetry pipe (src/services/telemetry).
 * That pipe feeds the admin "App Health" dashboards on our own backend; this
 * module feeds Firebase / GA4 so product gets funnels, retention, and Web
 * Vitals without standing up extra infrastructure. The two run side by side
 * and neither depends on the other.
 *
 * Everything here is best-effort and gated — a misconfigured project, an
 * unsupported browser, or a privacy blocker must never break the admin app:
 *   * VITE_FIREBASE_ANALYTICS_ENABLED === 'false' → hard off (keep the
 *     config populated in dev without shipping events).
 *   * No measurementId               → Analytics skipped (Performance still runs).
 *   * isSupported() === false (SSR / some privacy browsers) → skip cleanly.
 *   * Firebase app not configured     → both skipped (getFirebaseApp() null).
 */

import {
  getAnalytics,
  isSupported,
  logEvent,
  setUserId,
  type Analytics,
} from 'firebase/analytics'
import { getPerformance } from 'firebase/performance'

import { getFirebaseApp } from './config'

let analytics: Analytics | null = null
let initialised = false

/** Honour an explicit opt-out so a fully-configured project can still ship a
 *  build with Firebase telemetry silenced (e.g. preview/staging). Anything
 *  other than the literal string 'false' leaves it enabled. */
function analyticsEnabled(): boolean {
  return import.meta.env.VITE_FIREBASE_ANALYTICS_ENABLED !== 'false'
}

/**
 * One-shot boot wiring. Call from main.tsx alongside initialiseTelemetry().
 * Idempotent (guards a double hot-reload) and never throws — failures degrade
 * to "no Firebase analytics" rather than a broken render.
 */
export async function initialiseFirebaseAnalytics(): Promise<void> {
  if (initialised) return
  initialised = true

  if (!analyticsEnabled()) return

  const app = getFirebaseApp()
  if (!app) return // Config incomplete — config.ts already logged a warning.

  // Performance Monitoring auto-collects page-load timing + network request
  // traces with no further wiring. Initialise it first so it captures the
  // earliest possible window; it has no measurementId dependency. The
  // instance is a Firebase singleton (retained internally), so we don't
  // need to hold the reference — initialising for its side effect is enough.
  try {
    getPerformance(app)
  } catch (e) {
    console.warn('[firebase] performance init failed (non-fatal):', e)
  }

  // Analytics needs a GA4 measurementId AND a browser that supports it
  // (IndexedDB / cookies). isSupported() resolves false under SSR and some
  // hardened privacy modes — bail quietly there.
  try {
    if (!app.options.measurementId) {
      console.warn('[firebase] no measurementId — analytics disabled (set VITE_FIREBASE_MEASUREMENT_ID).')
      return
    }
    if (!(await isSupported())) return
    analytics = getAnalytics(app)
  } catch (e) {
    console.warn('[firebase] analytics init failed (non-fatal):', e)
  }
}

/** Safe event wrapper — no-ops until Analytics is initialised, so callers
 *  never have to null-check. Mirror GA4 event-naming conventions
 *  (snake_case, ≤40 chars). */
export function logAnalyticsEvent(
  name: string,
  params?: Record<string, unknown>,
): void {
  if (!analytics) return
  try {
    logEvent(analytics, name, params)
  } catch (e) {
    console.warn(`[firebase] logEvent("${name}") failed (non-fatal):`, e)
  }
}

/** SPA route changes don't auto-fire page_view — call this from the router
 *  on navigation so GA4 sees per-route screens, not just the initial load. */
export function trackPageView(path: string, title?: string): void {
  logAnalyticsEvent('page_view', {
    page_path: path,
    page_location: window.location.origin + path,
    page_title: title ?? document.title,
  })
}

/** Tie events to the signed-in admin so GA4 reports can segment by user.
 *  Pass null on logout to detach. Never send PII (email/name) — id only. */
export function setAnalyticsUser(userId: string | null): void {
  if (!analytics) return
  try {
    setUserId(analytics, userId)
  } catch (e) {
    console.warn('[firebase] setUserId failed (non-fatal):', e)
  }
}
